import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, STAGES, STAGE_PROBABILITY, SUB_TYPES, TICKET_STATUSES, PRIORITIES } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import AccountTasks from "@/components/AccountTasks";
import ProductCheckboxes from "@/components/ProductCheckboxes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Upload,
  FileText,
  Mail,
  Phone,
  Building2,
  CreditCard,
  BarChart3,
  Lock,
  ShieldCheck,
  Pencil,
  Edit2,
  X,
  Activity,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import CyberPostureTab from "@/components/CyberPostureTab";
import ChartMogulPanel from "@/components/ChartMogulPanel";
import DealEditDialog from "@/components/DealEditDialog";
import XeroLinkPanel from "@/components/XeroLinkPanel";
import XdrReviewTab from "@/components/XdrReviewTab";
import { useAuth } from "@/lib/auth";

const HealthDot = ({ h }) => {
  const c = h === "red" ? "bg-red-500" : h === "amber" ? "bg-amber-500" : "bg-emerald-500";
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${c}`} />;
};

const StatusBadge = ({ status }) => {
  const map = {
    prospect: "bg-slate-100 text-slate-700",
    active: "bg-emerald-50 text-emerald-700",
    at_risk: "bg-amber-50 text-amber-700",
    churned: "bg-red-50 text-red-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[11px] font-medium uppercase tracking-wider ${map[status] || map.prospect}`}>
      {status?.replace("_", " ")}
    </span>
  );
};

export default function AccountDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [account, setAccount] = useState(null);
  const [tab, setTab] = useState("overview");

  const reload = useCallback(async () => {
    const r = await api.get(`/accounts/${id}`);
    setAccount(r.data);
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  if (!account) return <div className="text-slate-500 font-mono text-sm">Loading account…</div>;

  return (
    <div className="space-y-6" data-testid="account-detail-page">
      <div className="flex items-center gap-3 text-sm text-slate-500">
        <Link to="/app/accounts" className="inline-flex items-center gap-1 hover:text-slate-900">
          <ArrowLeft className="h-3.5 w-3.5" /> Accounts
        </Link>
        <span>/</span>
        <span className="text-slate-900 font-medium">{account.name}</span>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-slate-950 to-blue-950 text-white p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3">
                <HealthDot h={account.health} />
                <h1 className="font-heading text-3xl font-bold tracking-tight">{account.name}</h1>
                <StatusBadge status={account.status} />
              </div>
              <div className="mt-2 text-sm text-slate-300 flex items-center gap-3 flex-wrap">
                {account.industry && <span>{account.industry}</span>}
                {account.website && <a href={`https://${account.website}`} target="_blank" rel="noreferrer" className="text-blue-300 hover:underline">{account.website} ↗</a>}
                <span className="font-mono text-slate-400">Owner · {account.owner_name || "—"}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-6">
              <Stat label="ARR" value={fmtMoney(account.arr)} />
              <Stat label="MRR" value={fmtMoney(account.mrr)} />
              <Stat label="Users" value={account.num_users || 0} />
              <Stat label="Open deals" value={account.rollups.open_deals} />
            </div>
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 gap-1 overflow-x-auto">
          {[
            ["overview", "Overview"],
            ["deals", "Deals"],
            ["contacts", "Contacts"],
            ["documents", "Documents"],
            ["chartmogul", "ChartMogul"],
            ["xero", "Xero Billing"],
            ["support", "Support"],
            ["stax", "Stax Products"],
            ["cyber", "Cyber Posture"],
            ["portal", "Customer Portal"],
            ["reports", "Reports"],
            ["xdr-review", "XDR Documents"],
          ].map(([v, label]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`tab-${v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 data-[state=active]:shadow-none px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-900"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <Overview account={account} reload={reload} onDelete={() => nav("/app/accounts")} />
        </TabsContent>
        <TabsContent value="deals" className="mt-6">
          <DealsTab accountId={id} accountName={account.name} reload={reload} />
        </TabsContent>
        <TabsContent value="contacts" className="mt-6">
          <ContactsTab accountId={id} />
        </TabsContent>
        <TabsContent value="documents" className="mt-6">
          <DocsTab accountId={id} />
        </TabsContent>
        <TabsContent value="chartmogul" className="mt-6">
          <ChartMogulPanel account={account} reload={reload} />
        </TabsContent>
        <TabsContent value="xero" className="mt-6">
          <XeroLinkPanel account={account} reload={reload} />
        </TabsContent>
        <TabsContent value="support" className="mt-6">
          <SupportTab accountId={id} />
        </TabsContent>
        <TabsContent value="stax" className="mt-6">
          <StaxTab accountId={id} />
        </TabsContent>
        <TabsContent value="cyber" className="mt-6">
          <CyberPostureTab accountId={id} />
        </TabsContent>
        <TabsContent value="portal" className="mt-6">
          <PortalTab account={account} reload={reload} />
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <ReportsTab accountId={id} accountName={account.name} />
        </TabsContent>
        <TabsContent value="xdr-review" className="mt-6">
          <XdrReviewTab accountId={id} accountName={account.name} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-blue-300">{label}</div>
    <div className="font-heading text-xl font-bold mt-1">{value}</div>
  </div>
);

/* ---------------- Overview ---------------- */
function Overview({ account, reload, onDelete }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Sales reps can transfer accounts to CSMs (see backend _resolve_owner_fields
  // — admin OR sales role is allowed).
  const canReassignOwner = isAdmin || user?.role === "sales";
  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [lookupLists, setLookupLists] = useState({ industries: { values: [] }, lifecycle_stages: { values: [] } });
  const [form, setForm] = useState({
    name: account.name,
    industry: account.industry || "",
    website: account.website || "",
    postcode: account.postcode || "",
    status: account.status,
    health: account.health,
    lifecycle_stage: account.lifecycle_stage || "Lead",
    owner_id: account.owner_id || "",
    num_users: account.num_users || 0,
    arr: account.arr,
    mrr: account.mrr,
    notes: account.notes || "",
  });

  useEffect(() => {
    if (canReassignOwner) {
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    }
    api.get("/lookup-lists").then((r) => setLookupLists(r.data)).catch(() => {});
  }, [canReassignOwner]);

  const save = async () => {
    const payload = {
      ...form,
      num_users: Number(form.num_users) || 0,
      arr: Number(form.arr) || 0,
      mrr: Number(form.mrr) || 0,
    };
    if (!canReassignOwner) delete payload.owner_id;
    if (!payload.owner_id) delete payload.owner_id;
    await api.patch(`/accounts/${account.id}`, payload);
    toast.success("Account updated");
    setEditing(false);
    reload();
  };

  const remove = async () => {
    if (!window.confirm("Delete this account and all related records?")) return;
    await api.delete(`/accounts/${account.id}`);
    toast.success("Account deleted");
    onDelete();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <Card className="lg:col-span-2 border-slate-200">
        <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-heading">Account details</CardTitle>
          {!editing ? (
            <Button data-testid="edit-account-btn" variant="outline" size="sm" onClick={() => setEditing(true)}>Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button data-testid="save-account-btn" size="sm" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-5">
          {editing ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name"><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></Field>
              <Field label="Industry">
                <Select value={form.industry || "__none__"} onValueChange={(v) => setForm({...form, industry: v === "__none__" ? "" : v})}>
                  <SelectTrigger data-testid="account-edit-industry"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {(lookupLists.industries?.values || []).map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                    {form.industry && !(lookupLists.industries?.values || []).includes(form.industry) && (
                      <SelectItem value={form.industry}>{form.industry} (legacy)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Website"><Input value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} /></Field>
              <Field label="Lifecycle Stage">
                <Select value={form.lifecycle_stage || "__none__"} onValueChange={(v) => setForm({...form, lifecycle_stage: v === "__none__" ? "" : v})}>
                  <SelectTrigger data-testid="account-edit-lifecycle"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {(lookupLists.lifecycle_stages?.values || []).map((v) => (
                      <SelectItem key={v} value={v}>{v}</SelectItem>
                    ))}
                    {form.lifecycle_stage && !(lookupLists.lifecycle_stages?.values || []).includes(form.lifecycle_stage) && (
                      <SelectItem value={form.lifecycle_stage}>{form.lifecycle_stage} (legacy)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["prospect","active","at_risk","churned"].map((s) => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Health">
                <Select value={form.health} onValueChange={(v) => setForm({...form, health: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["green","amber","red"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="ARR"><Input type="number" value={form.arr} onChange={(e) => setForm({...form, arr: e.target.value})} /></Field>
              <Field label="MRR"><Input type="number" value={form.mrr} onChange={(e) => setForm({...form, mrr: e.target.value})} /></Field>
              <Field label="No. Users"><Input data-testid="account-edit-num-users" type="number" min="0" value={form.num_users} onChange={(e) => setForm({...form, num_users: e.target.value})} /></Field>
              <Field label="Postcode">
                <Input
                  data-testid="account-edit-postcode"
                  value={form.postcode}
                  onChange={(e) => setForm({...form, postcode: e.target.value})}
                  placeholder="e.g. LS1 4DL"
                />
              </Field>
              {canReassignOwner && (
                <Field label="Owner">
                  <Select value={form.owner_id || "__none__"} onValueChange={(v) => setForm({...form, owner_id: v === "__none__" ? "" : v})}>
                    <SelectTrigger data-testid="account-edit-owner"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} · {u.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <div className="col-span-2"><Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></Field></div>
            </div>
          ) : (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <Row label="Industry" value={account.industry || "—"} />
              <Row label="Website" value={account.website || "—"} />
              <Row label="Lifecycle stage" value={account.lifecycle_stage || "—"} />
              <Row label="Owner" value={account.owner_name || "—"} />
              <Row label="ARR" value={fmtMoney(account.arr)} mono />
              <Row label="MRR" value={fmtMoney(account.mrr)} mono />
              <Row label="No. Users" value={account.num_users || 0} mono />
              <Row label="Postcode" value={account.postcode || "—"} mono />
              <Row label="Created" value={fmtDate(account.created_at)} />
              <Row label="Updated" value={fmtDate(account.updated_at)} />
              <div className="col-span-2">
                <dt className="text-xs uppercase tracking-wider text-slate-500 font-mono">Notes</dt>
                <dd className="mt-1 text-slate-700 whitespace-pre-wrap">{account.notes || "No notes."}</dd>
              </div>
            </dl>
          )}
        </CardContent>
      </Card>

      <div className="space-y-5">
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100"><CardTitle className="text-base font-heading">Activity rollup</CardTitle></CardHeader>
          <CardContent className="p-5 space-y-3 text-sm">
            <Row label="Open deals" value={account.rollups.open_deals} />
            <Row label="Open pipeline" value={fmtMoney(account.rollups.open_pipeline_value)} mono />
            <Row label="Won revenue" value={fmtMoney(account.rollups.won_revenue)} mono />
            <Row label="Open tickets" value={account.rollups.open_tickets} />
            <Row label="Contacts" value={account.rollups.contacts} />
            <Row label="Documents" value={account.rollups.documents} />
            <Row label="Stax active" value={account.rollups.stax_active} />
          </CardContent>
        </Card>
        <Button data-testid="delete-account-btn" variant="outline" className="w-full text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200" onClick={remove}>
          <Trash2 className="h-4 w-4 mr-2" /> Delete account
        </Button>
      </div>

      {/* Tasks & activity log (full-width below the overview grid) */}
      <div className="lg:col-span-3">
        <AccountTasks accountId={account.id} />
      </div>
    </div>
  );
}

const Row = ({ label, value, mono }) => (
  <div>
    <dt className="text-xs uppercase tracking-wider text-slate-500 font-mono">{label}</dt>
    <dd className={`text-slate-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</dd>
  </div>
);

const Field = ({ label, children, required }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1">
      {label}
      {required && <span className="text-rose-500" aria-label="required">*</span>}
    </Label>
    <div className="mt-1">{children}</div>
  </div>
);

const SectionHeading = ({ step, title, hint }) => (
  <div className="flex items-start gap-3 pb-1 border-b border-slate-100">
    <span className="shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-600 text-white text-[11px] font-mono font-semibold">{step}</span>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-heading font-semibold text-slate-900 leading-tight">{title}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  </div>
);

/* ---------------- Deals Tab ---------------- */
const SUMMARY_TONES = {
  blue: "border-blue-200 bg-blue-50/60 text-blue-900",
  emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-900",
  red: "border-red-200 bg-red-50/60 text-red-900",
  slate: "border-slate-200 bg-slate-50 text-slate-900",
  indigo: "border-indigo-200 bg-indigo-50/60 text-indigo-900",
};

const SummaryPill = ({ label, value, tone = "slate", sub }) => (
  <div className={`rounded-lg border px-4 py-3 ${SUMMARY_TONES[tone] || SUMMARY_TONES.slate}`} data-testid={`deals-summary-${label.toLowerCase()}`}>
    <div className="text-[10px] font-mono uppercase tracking-[0.15em] opacity-70">{label}</div>
    <div className="font-heading text-xl font-bold mt-0.5">{value}</div>
    {sub && <div className="text-[10px] font-mono opacity-60 mt-0.5">{sub}</div>}
  </div>
);

const STAGE_STYLES = {
  "Webinar / Introduction": "bg-slate-100 text-slate-700 border-slate-200",
  "Commercial Proposal": "bg-sky-50 text-sky-700 border-sky-200",
  "Proof of Concept": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Commercial Approval": "bg-violet-50 text-violet-700 border-violet-200",
  "Awaiting PO": "bg-amber-50 text-amber-800 border-amber-200",
  "Closed Won": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Closed Lost": "bg-red-50 text-red-700 border-red-200",
};

const StageChip = ({ stage }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${STAGE_STYLES[stage] || STAGE_STYLES["Webinar / Introduction"]}`}
    data-testid={`deal-stage-chip-${stage?.replace(/\s+/g, '-')}`}
  >
    {stage === "Closed Won" ? <CheckCircle2 className="h-3 w-3" /> : stage === "Closed Lost" ? <XCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
    {stage}
  </span>
);

const SortHeader = ({ label, sortKey, sortState, setSortState, align = "left", testid }) => {
  const active = sortState.key === sortKey;
  const dir = active ? sortState.dir : null;
  const onClick = () => {
    if (!active) return setSortState({ key: sortKey, dir: "asc" });
    if (dir === "asc") return setSortState({ key: sortKey, dir: "desc" });
    return setSortState({ key: "stage", dir: "asc" });
  };
  return (
    <th
      className={`px-4 py-3 select-none cursor-pointer group ${align === "right" ? "text-right" : "text-left"}`}
      onClick={onClick}
      data-testid={testid || `deals-sort-${sortKey}`}
    >
      <span className={`inline-flex items-center gap-1 ${active ? "text-blue-700" : "text-slate-500"} group-hover:text-blue-700 transition-colors`}>
        {label}
        {active
          ? (dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
          : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </span>
    </th>
  );
};

function DealsTab({ accountId, accountName, reload }) {
  const [deals, setDeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [sortState, setSortState] = useState({ key: "stage", dir: "asc" });
  const [staxCatalogue, setStaxCatalogue] = useState([]);
  const [form, setForm] = useState({
    name: "", revenue: "", cost: "", stage: "Webinar / Introduction", subscription_type: "New Business",
    probability: STAGE_PROBABILITY["Webinar / Introduction"] ?? 10,
    close_date: "", term_months: 12, licence_count: "", stax_products: [], notes: "",
  });

  const load = async () => {
    const r = await api.get(`/accounts/${accountId}/deals`);
    setDeals(r.data);
  };
  useEffect(() => { load(); }, [accountId]);

  // Load STAX catalogue once when the new-deal dialog is first opened. Pulls
  // from both STAX catalogues so the picker shows every option a lead could
  // have ticked — the newer `stax-services` list first, falling back to the
  // legacy `stax-products` list.
  useEffect(() => {
    if (!open || staxCatalogue.length > 0) return;
    (async () => {
      try {
        const [svcs, prods] = await Promise.allSettled([
          api.get("/stax-services"),
          api.get("/stax-products"),
        ]);
        const seen = new Set();
        const merged = [];
        [svcs, prods].forEach((r) => {
          if (r.status !== "fulfilled") return;
          (r.value.data || []).forEach((p) => {
            if (!p?.name || seen.has(p.name)) return;
            seen.add(p.name);
            merged.push(p);
          });
        });
        setStaxCatalogue(merged);
      } catch (_) { /* ignore — picker will show the empty state */ }
    })();
  }, [open, staxCatalogue.length]);

  const onStageChange = (v) => {
    // Probability is derived from stage and shown read-only, so always
    // overwrite it when the engineer picks a new stage.
    setForm({ ...form, stage: v, probability: STAGE_PROBABILITY[v] ?? form.probability });
  };

  // Mandatory-field gate — all primary fields must be filled plus at least
  // one STAX. Notes is intentionally optional per product rules.
  const canSubmit = (
    form.name.trim() !== "" &&
    form.revenue !== "" && Number(form.revenue) > 0 &&
    form.cost !== "" && Number(form.cost) >= 0 &&
    form.stage && form.subscription_type &&
    form.close_date &&
    form.term_months !== "" && Number(form.term_months) >= 1 &&
    Array.isArray(form.stax_products) && form.stax_products.length > 0
  );

  const create = async () => {
    if (!canSubmit) return toast.error("Fill every field and pick at least one STAX product");
    const term = Math.max(1, Math.min(60, Number(form.term_months) || 12));
    try {
      await api.post(`/accounts/${accountId}/deals`, {
        ...form,
        revenue: Number(form.revenue) || 0,
        cost: Number(form.cost) || 0,
        term_months: term,
        licence_count: form.licence_count === "" ? null : Number(form.licence_count),
        stax_products: form.stax_products,
      });
      toast.success("Deal created");
      setOpen(false);
      setForm({
        name: "", revenue: "", cost: "", stage: "Webinar / Introduction", subscription_type: "New Business",
        probability: STAGE_PROBABILITY["Webinar / Introduction"] ?? 10,
        close_date: "", term_months: 12, licence_count: "", stax_products: [], notes: "",
      });
      load(); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create deal");
    }
  };

  const updateStage = async (dealId, stage) => {
    await api.patch(`/deals/${dealId}`, { stage });
    load(); reload();
  };

  const remove = async (dealId) => {
    await api.delete(`/deals/${dealId}`);
    toast.success("Deal removed");
    load(); reload();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{deals.length} deals on this account</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-deal-btn" className="gap-2 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> New Deal</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0">
            <DialogHeader className="px-6 pt-5 pb-4 border-b border-slate-200/70 bg-gradient-to-br from-slate-50 to-white sticky top-0 z-10">
              <DialogTitle className="text-lg font-heading text-slate-900">New deal</DialogTitle>
              <div className="text-xs text-slate-500 mt-0.5">
                Account · <span className="font-medium text-slate-700">{accountName}</span>
              </div>
            </DialogHeader>
            <div className="px-6 pt-5 pb-2 space-y-6">
              {/* ---------- Section 1 — Essentials ---------- */}
              <section className="space-y-3">
                <SectionHeading step="1" title="Essentials" hint="Name the deal and size it" />
                <Field label="Deal name" required>
                  <Input data-testid="deal-form-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Cyber Security Stax — annual" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Revenue (£)" required>
                    <Input data-testid="deal-form-revenue" type="number" min="1" step="0.01" value={form.revenue} onChange={(e) => setForm({...form, revenue: e.target.value})} placeholder="0.00" />
                  </Field>
                  <Field label="Cost (£)" required>
                    <Input data-testid="deal-form-cost" type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm({...form, cost: e.target.value})} placeholder="0.00" />
                  </Field>
                </div>
              </section>

              {/* ---------- Section 2 — Stage ---------- */}
              <section className="space-y-3">
                <SectionHeading step="2" title="Stage & term" hint="Probability is derived automatically" />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Stage" required>
                    <Select value={form.stage} onValueChange={onStageChange}>
                      <SelectTrigger data-testid="deal-form-stage"><SelectValue /></SelectTrigger>
                      <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s} {STAGE_PROBABILITY[s] != null ? `· ${STAGE_PROBABILITY[s]}%` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Subscription type" required>
                    <Select value={form.subscription_type} onValueChange={(v) => setForm({...form, subscription_type: v})}>
                      <SelectTrigger data-testid="deal-form-sub-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{SUB_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Probability">
                    <div
                      data-testid="deal-form-probability"
                      className="h-10 flex items-center justify-between px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700"
                      title="Probability is derived automatically from the chosen stage"
                    >
                      <span className="font-mono">{form.probability}%</span>
                      <span className="text-[9px] font-mono uppercase tracking-wider text-slate-400">Auto</span>
                    </div>
                  </Field>
                  <Field label="Close date" required>
                    <Input data-testid="deal-form-close-date" type="date" value={form.close_date} onChange={(e) => setForm({...form, close_date: e.target.value})} />
                  </Field>
                  <Field label="Term (months)" required>
                    <Input data-testid="deal-form-term" type="number" min="1" max="60" value={form.term_months} onChange={(e) => setForm({...form, term_months: e.target.value})} />
                  </Field>
                </div>
                <div className="text-[11px] font-mono text-slate-500 bg-slate-50 border border-slate-100 rounded-md px-3 py-2">
                  Renewal due ≈ close date + {form.term_months || 12} months · every Xentra deal is renewable.
                </div>
              </section>

              {/* ---------- Section 3 — STAX ---------- */}
              <section className="space-y-3">
                <SectionHeading step="3" title="STAX products" hint="Pick everything the customer is buying" />
                <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="deal-form-stax">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50/70">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
                      Selected · <span className="text-slate-900">{form.stax_products.length}</span>
                    </div>
                    <div className={`text-[11px] font-mono ${form.stax_products.length === 0 ? "text-amber-600" : "text-emerald-600"}`}>
                      {form.stax_products.length === 0 ? "At least one required" : "Ready"}
                    </div>
                  </div>
                  <div className="p-3 max-h-56 overflow-y-auto">
                    <ProductCheckboxes
                      products={staxCatalogue}
                      selected={form.stax_products}
                      onChange={(names) => setForm({ ...form, stax_products: names })}
                      testidPrefix="deal-form-stax"
                    />
                  </div>
                </div>
                <Field label="Licence count">
                  <Input data-testid="deal-form-licence-count" type="number" min="0" step="1" value={form.licence_count} onChange={(e) => setForm({...form, licence_count: e.target.value})} placeholder="Total seats across the chosen products — e.g. 250" />
                </Field>
              </section>

              {/* ---------- Section 4 — Notes ---------- */}
              <section className="space-y-3 pb-2">
                <SectionHeading step="4" title="Notes" hint="Optional context — blockers, next step, who's championing" />
                <Textarea data-testid="deal-form-notes" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Decision maker · competitors in play · next check-in…" />
              </section>
            </div>
            <DialogFooter className="px-6 py-4 border-t border-slate-200/70 bg-slate-50/50 sticky bottom-0">
              <div className="flex-1 text-[11px] font-mono text-slate-500 self-center">
                {canSubmit
                  ? <span className="text-emerald-700">All set — ready to create.</span>
                  : <span className="text-amber-700">Fill every required field + pick ≥1 STAX.</span>}
              </div>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="deal-form-submit" onClick={create} disabled={!canSubmit} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">Create deal</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {deals.length === 0 ? (
        <Card className="border-slate-200 p-10 text-center">
          <BarChart3 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">No deals yet</div>
          <div className="text-sm text-slate-500 mt-1">Track revenue, GP and weighted forecast for this account.</div>
        </Card>
      ) : (
        (() => {
          const isClosed = (s) => s === "Closed Won" || s === "Closed Lost";
          const stageIdx = (s) => {
            const i = STAGES.indexOf(s);
            return i === -1 ? 999 : i;
          };
          const num = (v) => Number(v) || 0;
          const dateVal = (d) => {
            const s = d.close_date || d.expected_close_date || "";
            return s ? new Date(s).getTime() : 0;
          };
          const cmp = (a, b) => {
            const { key, dir } = sortState;
            let av, bv;
            switch (key) {
              case "stage": av = stageIdx(a.stage); bv = stageIdx(b.stage); break;
              case "name": av = (a.name || "").toLowerCase(); bv = (b.name || "").toLowerCase(); break;
              case "type": av = (a.subscription_type || "").toLowerCase(); bv = (b.subscription_type || "").toLowerCase(); break;
              case "revenue": av = num(a.revenue); bv = num(b.revenue); break;
              case "mrr": av = num(a.mrr); bv = num(b.mrr); break;
              case "forecast": av = num(a.forecast_value); bv = num(b.forecast_value); break;
              case "close_date": av = dateVal(a); bv = dateVal(b); break;
              case "renews": av = a.end_date ? new Date(a.end_date).getTime() : 0; bv = b.end_date ? new Date(b.end_date).getTime() : 0; break;
              default: av = 0; bv = 0;
            }
            if (av < bv) return dir === "asc" ? -1 : 1;
            if (av > bv) return dir === "asc" ? 1 : -1;
            // Tie-breaker: name asc
            const an = (a.name || "").toLowerCase();
            const bn = (b.name || "").toLowerCase();
            return an < bn ? -1 : an > bn ? 1 : 0;
          };
          const sorted = [...deals].sort(cmp);

          // Summary strip
          const openDeals = deals.filter(d => !isClosed(d.stage));
          const wonDeals = deals.filter(d => d.stage === "Closed Won");
          const lostDeals = deals.filter(d => d.stage === "Closed Lost");
          const pipelineValue = openDeals.reduce((s, d) => s + num(d.revenue), 0);
          const weightedValue = openDeals.reduce((s, d) => s + num(d.forecast_value), 0);
          const wonValue = wonDeals.reduce((s, d) => s + num(d.revenue), 0);

          return (
            <div className="space-y-3">
              {/* Summary strip */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="deals-summary">
                <SummaryPill label="Open" value={openDeals.length} tone="blue" />
                <SummaryPill label="Won" value={wonDeals.length} tone="emerald" />
                <SummaryPill label="Lost" value={lostDeals.length} tone="red" />
                <SummaryPill label="Pipeline" value={fmtMoney(pipelineValue)} tone="slate" />
                <SummaryPill label="Weighted" value={fmtMoney(weightedValue)} tone="indigo" sub={`Won ${fmtMoney(wonValue)}`} />
              </div>

              <Card className="border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="deals-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider">
                        <SortHeader label="Deal" sortKey="name" sortState={sortState} setSortState={setSortState} />
                        <SortHeader label="Stage" sortKey="stage" sortState={sortState} setSortState={setSortState} />
                        <SortHeader label="Close" sortKey="close_date" sortState={sortState} setSortState={setSortState} />
                        <SortHeader label="Type" sortKey="type" sortState={sortState} setSortState={setSortState} />
                        <SortHeader label="Revenue" sortKey="revenue" sortState={sortState} setSortState={setSortState} align="right" />
                        <SortHeader label="MRR" sortKey="mrr" sortState={sortState} setSortState={setSortState} align="right" />
                        <SortHeader label="Forecast" sortKey="forecast" sortState={sortState} setSortState={setSortState} align="right" />
                        <SortHeader label="Renews" sortKey="renews" sortState={sortState} setSortState={setSortState} />
                        <th className="px-4 py-3 w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((d) => {
                        const closed = isClosed(d.stage);
                        const closeDateStr = d.close_date || d.expected_close_date;
                        return (
                          <tr
                            key={d.id}
                            className={`border-b border-slate-100 hover:bg-blue-50/30 transition-colors ${d.stage === "Closed Lost" ? "opacity-70" : ""}`}
                            data-testid={`deal-row-${d.id}`}
                          >
                            {/* Deal name */}
                            <td className="px-4 py-3 max-w-[280px]">
                              <button
                                onClick={() => setEditingDeal(d)}
                                className="text-left font-medium text-slate-900 hover:text-blue-700 truncate block w-full"
                                data-testid={`deal-name-${d.id}`}
                              >
                                {d.name}
                              </button>
                              <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                                <span className="font-mono">{d.probability ?? 0}%</span>
                                <span>·</span>
                                <span className="truncate">{d.owner_name || "unassigned"}</span>
                                {d.is_auto_renewal && (
                                  <span className="ml-1 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                    auto-renew
                                  </span>
                                )}
                              </div>
                            </td>
                            {/* Stage chip + inline select on hover */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <StageChip stage={d.stage} />
                                <Select value={d.stage} onValueChange={(v) => updateStage(d.id, v)}>
                                  <SelectTrigger
                                    className="h-6 w-6 p-0 border-slate-200 opacity-40 hover:opacity-100"
                                    data-testid={`deal-stage-select-${d.id}`}
                                    aria-label="Change stage"
                                  >
                                    <Edit2 className="h-3 w-3 mx-auto text-slate-500" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            </td>
                            {/* Close date — actual or expected */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              {closeDateStr ? (
                                <>
                                  <div className={`font-mono text-sm ${closed ? "text-slate-900" : "text-slate-700"}`}>{fmtDate(closeDateStr)}</div>
                                  <div className={`text-[10px] font-mono uppercase tracking-wider ${closed ? (d.stage === "Closed Won" ? "text-emerald-600" : "text-red-500") : "text-slate-400"}`}>
                                    {closed ? "closed" : "expected"}
                                  </div>
                                </>
                              ) : (
                                <span className="text-slate-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-600">{d.subscription_type}</td>
                            <td className="px-4 py-3 text-right font-mono text-slate-900">{fmtMoney(d.revenue)}</td>
                            <td className="px-4 py-3 text-right font-mono text-slate-700">{fmtMoney(d.mrr)}</td>
                            <td className="px-4 py-3 text-right font-mono text-blue-700">{fmtMoney(d.forecast_value)}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              {d.end_date ? (
                                <>
                                  <div className="font-mono text-slate-700">{fmtDate(d.end_date)}</div>
                                  <div className="text-[10px] font-mono text-slate-400">{d.term_months || 12}m term</div>
                                </>
                              ) : (
                                <span className="text-slate-400 text-xs">—</span>
                              )}
                              {d.renewal_deal_id && (
                                <div className="text-[10px] font-mono text-emerald-700 mt-0.5">↻ renewal queued</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => setEditingDeal(d)} data-testid={`edit-deal-${d.id}`}>
                                  <Pencil className="h-4 w-4 text-slate-400 hover:text-blue-600" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => remove(d.id)} data-testid={`delete-deal-${d.id}`}>
                                  <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          );
        })()
      )}

      {editingDeal && (
        <DealEditDialog
          deal={editingDeal}
          onClose={() => setEditingDeal(null)}
          onSaved={async () => { await load(); reload(); setEditingDeal(null); }}
        />
      )}
    </div>
  );
}

/* ---------------- Contacts Tab ---------------- */
const EMPTY_CONTACT = {
  first_name: "", last_name: "", email: "", phone: "",
  mobile: "", direct_dial: "", hq_phone: "",
  job_title: "", notes: "",
  is_primary: false, is_decision_maker: false, is_billing: false, is_support: false,
  receive_threat_advisories: false,
  tags: [],
};

function ContactsTab({ accountId }) {
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // contact being edited, or null
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [allTags, setAllTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  // Activity timeline drawer state
  const [activityFor, setActivityFor] = useState(null); // contact object whose timeline is open

  const load = async () => {
    const r = await api.get(`/accounts/${accountId}/contacts`);
    setContacts(r.data);
  };
  useEffect(() => { load(); }, [accountId]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_CONTACT);
    setTagInput("");
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY_CONTACT, ...c, tags: c.tags || [] });
    setTagInput("");
    setOpen(true);
  };
  const closeDialog = () => {
    setOpen(false);
    setEditing(null);
    setForm(EMPTY_CONTACT);
    setTagInput("");
  };

  // ---- Tag chip handling ----
  const addTag = () => {
    const v = tagInput.trim();
    if (!v) return;
    const existing = (form.tags || []).map((t) => t.toLowerCase());
    if (existing.includes(v.toLowerCase())) {
      setTagInput("");
      return;
    }
    setForm((f) => ({ ...f, tags: [...(f.tags || []), v] }));
    setTagInput("");
  };
  const removeTag = (idx) => {
    setForm((f) => ({ ...f, tags: (f.tags || []).filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.first_name || !form.last_name) return toast.error("Name required");
    // Only send string fields we might have edited — Backend uses exclude_unset
    // for PATCH, so we send the full form dict either way (it treats each
    // empty-string as an explicit clear on notes/phones, which is what the user wants).
    const payload = {
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email || null,
      phone: form.phone || null,
      mobile: form.mobile || null,
      direct_dial: form.direct_dial || null,
      hq_phone: form.hq_phone || null,
      job_title: form.job_title || null,
      notes: form.notes || null,
      is_primary: !!form.is_primary,
      is_decision_maker: !!form.is_decision_maker,
      is_billing: !!form.is_billing,
      is_support: !!form.is_support,
      receive_threat_advisories: !!form.receive_threat_advisories,
      tags: form.tags || [],
    };
    try {
      if (editing) {
        await api.patch(`/contacts/${editing.id}`, payload);
        toast.success("Contact updated");
      } else {
        await api.post(`/accounts/${accountId}/contacts`, payload);
        toast.success("Contact added");
      }
      closeDialog();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contact deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{contacts.length} contacts</div>
        <Button data-testid="new-contact-btn" onClick={openCreate} className="gap-2 bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4" /> New Contact
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
        <DialogContent className="max-w-2xl" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit contact" : "New contact"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name">
                <Input data-testid="contact-form-first" value={form.first_name}
                       onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </Field>
              <Field label="Last name">
                <Input data-testid="contact-form-last" value={form.last_name}
                       onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </Field>
            </div>
            <Field label="Job title">
              <Input value={form.job_title}
                     onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input type="email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone (main)">
                <Input data-testid="contact-form-phone" value={form.phone}
                       onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>
              <Field label="Mobile">
                <Input data-testid="contact-form-mobile" value={form.mobile}
                       onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
              </Field>
              <Field label="Direct dial (DD)">
                <Input data-testid="contact-form-dd" value={form.direct_dial}
                       onChange={(e) => setForm({ ...form, direct_dial: e.target.value })} />
              </Field>
              <Field label="HQ / switchboard">
                <Input data-testid="contact-form-hq" value={form.hq_phone}
                       onChange={(e) => setForm({ ...form, hq_phone: e.target.value })} />
              </Field>
            </div>
            <Field label="Notes">
              <Textarea rows={3} data-testid="contact-form-notes" placeholder="e.g. only works three days a week; prefers WhatsApp"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Field>
            <Field label="Tags">
              <div>
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="contact-tags-chips">
                  {(form.tags || []).length === 0 && (
                    <span className="text-xs text-slate-400 italic">e.g. VIP, technical, procurement…</span>
                  )}
                  {(form.tags || []).map((t, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-800 border border-blue-200"
                      data-testid={`contact-tag-chip-${t}`}
                    >
                      {t}
                      <button
                        type="button"
                        onClick={() => removeTag(i)}
                        className="hover:text-red-600 leading-none"
                        title="Remove"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    list="contact-tag-suggestions"
                    placeholder="Add a tag and press Enter"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); addTag(); }
                    }}
                    className="h-8"
                    data-testid="contact-tag-input"
                  />
                  <datalist id="contact-tag-suggestions">
                    {allTags.map((t) => <option key={t} value={t} />)}
                  </datalist>
                  <Button type="button" size="sm" variant="outline" onClick={addTag} data-testid="contact-tag-add-btn">
                    Add
                  </Button>
                </div>
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-2 pt-2">
              {[
                ["is_primary", "Primary"],
                ["is_decision_maker", "Decision maker"],
                ["is_billing", "Billing"],
                ["is_support", "Support"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox checked={!!form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: !!v })} />
                  {label}
                </label>
              ))}
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700 pt-2 border-t border-slate-100 mt-2" data-testid="contact-form-rta-wrapper">
              <Checkbox
                checked={!!form.receive_threat_advisories}
                onCheckedChange={(v) => setForm({ ...form, receive_threat_advisories: !!v })}
                data-testid="contact-form-rta-checkbox"
                className="mt-0.5"
              />
              <span className="leading-snug">
                <span className="font-medium text-rose-700">Receive Threat Advisories</span>
                <span className="block text-[11px] text-slate-500">Auto-add this contact as a default recipient whenever a Threat Advisory is sent out. Requires an email address.</span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button data-testid="contact-form-submit" onClick={save} className="bg-blue-600 hover:bg-blue-700">
              {editing ? "Save changes" : "Add contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {contacts.length === 0 ? (
        <Card className="p-10 text-center border-slate-200 text-slate-500 text-sm">No contacts yet.</Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {contacts.map((c) => (
            <Card key={c.id} className="border-slate-200 p-4 flex items-start justify-between" data-testid={`contact-card-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900">{c.first_name} {c.last_name}</div>
                <div className="text-xs text-slate-500">{c.job_title || "—"}</div>
                <div className="mt-2 space-y-1 text-sm">
                  {c.email && <div className="flex items-center gap-2 text-slate-700"><Mail className="h-3.5 w-3.5 text-slate-400" />{c.email}</div>}
                  {c.phone && <div className="flex items-center gap-2 text-slate-700"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.phone}<span className="text-[10px] text-slate-400 uppercase tracking-wider ml-1">main</span></div>}
                  {c.mobile && <div className="flex items-center gap-2 text-slate-700"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.mobile}<span className="text-[10px] text-slate-400 uppercase tracking-wider ml-1">mob</span></div>}
                  {c.direct_dial && <div className="flex items-center gap-2 text-slate-700"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.direct_dial}<span className="text-[10px] text-slate-400 uppercase tracking-wider ml-1">DD</span></div>}
                  {c.hq_phone && <div className="flex items-center gap-2 text-slate-700"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.hq_phone}<span className="text-[10px] text-slate-400 uppercase tracking-wider ml-1">HQ</span></div>}
                </div>
                {c.notes && (
                  <div className="mt-2 text-xs text-slate-500 border-l-2 border-slate-200 pl-2 italic whitespace-pre-line line-clamp-3">
                    {c.notes}
                  </div>
                )}
                <div className="mt-3 flex gap-1.5 flex-wrap">
                  {c.is_primary && <Tag>Primary</Tag>}
                  {c.is_decision_maker && <Tag>Decision maker</Tag>}
                  {c.is_billing && <Tag color="amber">Billing</Tag>}
                  {c.is_support && <Tag color="emerald">Support</Tag>}
                  {c.receive_threat_advisories && <Tag color="rose">Threat Advisories</Tag>}
                  {(c.tags || []).map((t) => (
                    <span
                      key={t}
                      className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
                      data-testid={`contact-card-tag-${t}`}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button variant="ghost" size="icon" onClick={() => setActivityFor(c)} title="View activity timeline" data-testid={`activity-contact-${c.id}`}>
                  <Activity className="h-4 w-4 text-slate-400 hover:text-blue-600" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(c)} title="Edit" data-testid={`edit-contact-${c.id}`}>
                  <Edit2 className="h-4 w-4 text-slate-400 hover:text-blue-600" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(c.id)} title="Delete" data-testid={`delete-contact-${c.id}`}>
                  <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Activity timeline dialog */}
      {activityFor && (
        <ContactActivityDialog
          contact={activityFor}
          open={!!activityFor}
          onOpenChange={(v) => !v && setActivityFor(null)}
        />
      )}
    </div>
  );
}

const Tag = ({ children, color = "blue" }) => (
  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider bg-${color}-50 text-${color}-700 border border-${color}-100`}>{children}</span>
);

/* ---------------- Contact Activity Timeline dialog ---------------- */
function ContactActivityDialog({ contact, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!contact?.id) return;
    setLoading(true);
    api.get(`/contacts/${contact.id}/activity`)
      .then((r) => setData(r.data))
      .catch(() => setData({ entries: [], counts: { total: 0, tickets: 0, tasks: 0, deals: 0 } }))
      .finally(() => setLoading(false));
  }, [contact?.id]);

  const entries = (data?.entries || []).filter((e) => filter === "all" || e.kind === filter);
  const KIND_META = {
    ticket: { label: "Ticket", tone: "text-rose-700 bg-rose-50 border-rose-200", icon: "T" },
    task:   { label: "Note / Task", tone: "text-blue-700 bg-blue-50 border-blue-200", icon: "N" },
    deal:   { label: "Deal", tone: "text-emerald-700 bg-emerald-50 border-emerald-200", icon: "£" },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="contact-activity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" /> Activity · {contact.first_name} {contact.last_name}
          </DialogTitle>
          {contact.email && (
            <div className="text-xs text-slate-500 font-mono">{contact.email}</div>
          )}
        </DialogHeader>

        {loading ? (
          <div className="p-6 text-sm text-slate-500 text-center">Loading activity…</div>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                ["all",     `All (${data?.counts?.total ?? 0})`],
                ["ticket",  `Tickets (${data?.counts?.tickets ?? 0})`],
                ["task",    `Notes & Tasks (${data?.counts?.tasks ?? 0})`],
                ["deal",    `Deals (${data?.counts?.deals ?? 0})`],
              ].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setFilter(v)}
                  data-testid={`contact-activity-filter-${v}`}
                  className={`px-3 py-1 rounded-full text-xs font-medium border ${
                    filter === v
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {entries.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500" data-testid="contact-activity-empty">
                {data?.counts?.total === 0
                  ? "No activity yet for this contact. Tickets they raise, tasks logged against them, and deals on their account will appear here."
                  : "Nothing matches this filter."}
              </div>
            ) : (
              <ol className="relative border-l-2 border-slate-100 ml-3 space-y-4" data-testid="contact-activity-list">
                {entries.map((e) => {
                  const meta = KIND_META[e.kind] || { label: e.kind, tone: "text-slate-700 bg-slate-50 border-slate-200", icon: "?" };
                  return (
                    <li key={`${e.kind}-${e.id}`} className="pl-5 relative" data-testid={`contact-activity-entry-${e.kind}-${e.id}`}>
                      <span className={`absolute -left-[13px] top-1 h-6 w-6 rounded-full border flex items-center justify-center text-[10px] font-bold ${meta.tone}`}>
                        {meta.icon}
                      </span>
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{e.title}</div>
                          {e.subtitle && (
                            <div className="text-xs text-slate-500 mt-0.5">{e.subtitle}</div>
                          )}
                          {e.meta?.notes && e.kind === "task" && (
                            <div className="text-xs text-slate-600 mt-1 line-clamp-3 whitespace-pre-line bg-slate-50 rounded px-2 py-1 border border-slate-100">
                              {e.meta.notes}
                            </div>
                          )}
                        </div>
                        <div className="text-[10px] font-mono text-slate-400 whitespace-nowrap">
                          {e.occurred_at ? fmtDate(e.occurred_at) : "—"}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Documents Tab ---------------- */
function DocsTab({ accountId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [tag, setTag] = useState("contract");

  const load = async () => {
    const r = await api.get(`/accounts/${accountId}/documents`);
    setDocs(r.data);
  };
  useEffect(() => { load(); }, [accountId]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/accounts/${accountId}/documents?tag=${tag}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <Card className="border-slate-200 p-4 flex items-center gap-3 flex-wrap">
        <Select value={tag} onValueChange={setTag}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["contract","proposal","order_form","invoice","report","other"].map((t) => <SelectItem key={t} value={t}>{t.replace("_"," ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer">
          <Upload className="h-4 w-4" />
          {uploading ? "Uploading…" : "Upload document"}
          <input data-testid="doc-upload-input" type="file" className="hidden" onChange={onFile} disabled={uploading} />
        </label>
        <div className="text-xs text-slate-500">PDF, contracts, proposals, invoices.</div>
      </Card>

      {docs.length === 0 ? (
        <Card className="p-10 text-center border-slate-200 text-slate-500 text-sm">No documents yet.</Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">File</th>
                <th className="px-4 py-3">Tag</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Uploaded</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-slate-900">{d.filename}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 ml-6">{d.uploaded_by}</div>
                  </td>
                  <td className="px-4 py-3"><Tag>{d.tag}</Tag></td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{Math.round((d.size||0)/1024)} KB</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{fmtDate(d.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="icon" onClick={() => remove(d.id)}>
                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

/* ---------------- Stax Tab ---------------- */
function StaxTab({ accountId }) {
  const [items, setItems] = useState([]);
  const load = async () => {
    const r = await api.get(`/accounts/${accountId}/stax`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, [accountId]);

  const toggle = async (product_id, enabled) => {
    await api.post(`/accounts/${accountId}/stax/toggle`, { product_id, enabled });
    load();
  };

  const grouped = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="stax-checklist">
      {Object.keys(grouped).length === 0 && (
        <Card className="p-10 text-center border-slate-200 text-slate-500 text-sm">No Stax catalog yet — visit Stax Catalog page.</Card>
      )}
      {Object.entries(grouped).map(([cat, list]) => (
        <Card key={cat} className="border-slate-200">
          <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-blue-600" />
              <CardTitle className="text-base font-heading">{cat} STAX</CardTitle>
            </div>
            <span className="text-xs text-slate-500 font-mono">{list.filter((i) => i.enabled).length}/{list.length} active</span>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {list.map((it) => (
              <div
                key={it.product_id}
                data-testid={`stax-item-${it.product_id}`}
                className={`flex items-start justify-between p-3 rounded-md border transition-colors ${it.enabled ? "bg-blue-50/40 border-blue-100" : "bg-white border-slate-100"}`}
              >
                <div className="flex items-start gap-3 flex-1">
                  <Checkbox
                    data-testid={`stax-toggle-${it.product_id}`}
                    checked={it.enabled}
                    onCheckedChange={(v) => toggle(it.product_id, !!v)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">{it.name}</div>
                    {it.description && <div className="text-xs text-slate-500 mt-0.5">{it.description}</div>}
                    {it.first_used_date && (
                      <div className="mt-1 text-[11px] font-mono text-blue-700">
                        Active since {fmtDate(it.first_used_date)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ---------------- Support Tab ---------------- */
function SupportTab({ accountId }) {
  const [tickets, setTickets] = useState([]);
  const [products, setProducts] = useState([]);
  const [usage, setUsage] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject:"", description:"", priority:"Medium", status:"New", category:"Support", product_id:"", sla_due:"" });

  const load = async () => {
    const [t, p, u] = await Promise.all([
      api.get(`/accounts/${accountId}/tickets`),
      api.get(`/stax-products`),
      api.get(`/accounts/${accountId}/time-usage`).catch(() => ({ data: null })),
    ]);
    setTickets(t.data);
    setProducts(p.data);
    setUsage(u.data);
  };
  useEffect(() => { load(); }, [accountId]);

  const create = async () => {
    if (!form.subject) return toast.error("Subject required");
    await api.post(`/accounts/${accountId}/tickets`, {
      ...form,
      product_id: form.product_id || null,
    });
    toast.success("Ticket created");
    setOpen(false);
    setForm({ subject:"", description:"", priority:"Medium", status:"New", category:"Support", product_id:"", sla_due:"" });
    load();
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/tickets/${id}`, { status });
    load();
  };

  return (
    <div className="space-y-4">
      {usage && usage.total_minutes > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="account-time-usage">
          <UsageCard label="Support" value={`${usage.support_hours}h`} tone="text-indigo-700 bg-indigo-50 border-indigo-100" />
          <UsageCard label="Service (Managed)" value={`${usage.service_hours}h`} tone="text-emerald-700 bg-emerald-50 border-emerald-100" />
          <UsageCard label="Projects" value={`${usage.project_hours}h`} tone="text-blue-700 bg-blue-50 border-blue-100" />
          <UsageCard label="Total time" value={`${usage.total_hours}h`} tone="text-slate-800 bg-slate-50 border-slate-200" />
        </div>
      )}
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">{tickets.length} tickets</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-ticket-btn" className="gap-2 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> New Ticket</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Open a support ticket</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Field label="Subject"><Input data-testid="ticket-form-subject" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} /></Field>
              <Field label="Description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Priority">
                  <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Status">
                  <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TICKET_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Category">
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger data-testid="ticket-form-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Support">Support</SelectItem>
                      <SelectItem value="Service">Service</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="SLA Due"><Input type="date" value={form.sla_due} onChange={(e) => setForm({...form, sla_due: e.target.value})} /></Field>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <Field label="Product (Stax)">
                  <Select value={form.product_id || "none"} onValueChange={(v) => setForm({...form, product_id: v === "none" ? "" : v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="ticket-form-submit" onClick={create} className="bg-blue-600 hover:bg-blue-700">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {tickets.length === 0 ? (
        <Card className="p-10 text-center border-slate-200 text-slate-500 text-sm">No support tickets yet.</Card>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => <TicketRow key={t.id} t={t} onStatus={updateStatus} />)}
        </div>
      )}
    </div>
  );
}

function UsageCard({ label, value, tone }) {
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${tone}`}>
      <div className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-0.5 text-lg font-bold tabular-nums font-heading">{value}</div>
    </div>
  );
}

export function TicketRow({ t, onStatus }) {
  const priColor = { Urgent:"text-red-600 bg-red-50 border-red-200", High:"text-amber-700 bg-amber-50 border-amber-200", Medium:"text-blue-700 bg-blue-50 border-blue-100", Low:"text-slate-600 bg-slate-50 border-slate-200" };
  const stColor = { New:"bg-blue-100 text-blue-800", Open:"bg-amber-100 text-amber-800", "Waiting on Customer":"bg-purple-100 text-purple-800", "Waiting on Internal":"bg-orange-100 text-orange-800", Resolved:"bg-emerald-100 text-emerald-800", Closed:"bg-slate-100 text-slate-700" };
  return (
    <Card className="border-slate-200 p-4 flex items-start gap-4 hover:border-blue-200 transition-colors">
      <div className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${priColor[t.priority] || priColor.Medium}`}>{t.priority}</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 flex items-center gap-2">
          {t.subject}
          {t.category === "Service" && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">Service</span>}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">{t.description || "—"}</div>
        <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500 font-mono">
          {t.product_name && <span>· {t.product_name}</span>}
          {t.account_name && <span>· {t.account_name}</span>}
          <span>· {fmtDate(t.created_at)}</span>
        </div>
      </div>
      {onStatus ? (
        <Select value={t.status} onValueChange={(v) => onStatus(t.id, v)}>
          <SelectTrigger className="h-8 w-44 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{TICKET_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <span className={`px-2 py-1 rounded text-[10px] font-semibold uppercase ${stColor[t.status] || stColor.New}`}>{t.status}</span>
      )}
    </Card>
  );
}

/* ---------------- Reports Tab ---------------- */
function ReportsTab({ accountId, accountName }) {
  const [reports, setReports] = useState([]);
  const load = async () => {
    const r = await api.get(`/reports`, { params: { account_id: accountId } });
    setReports(r.data);
  };
  useEffect(() => { load(); }, [accountId]);

  const generate = async () => {
    await api.post(`/reports/account-summary/${accountId}`);
    toast.success("Report generated");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/reports/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-500">Saved reports for {accountName}</div>
        <Button data-testid="generate-report-btn" onClick={generate} className="bg-blue-600 hover:bg-blue-700 gap-2">
          <BarChart3 className="h-4 w-4" /> Generate Account Summary
        </Button>
      </div>
      {reports.length === 0 ? (
        <Card className="p-10 text-center border-slate-200 text-slate-500 text-sm">No saved reports yet.</Card>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <Card key={r.id} className="border-slate-200 p-4">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-slate-900">{r.name}</div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5">{r.type} · by {r.generated_by} · {fmtDate(r.created_at)}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => remove(r.id)}>
                  <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                </Button>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <ReportStat label="Deals" value={r.payload.deals_total} />
                <ReportStat label="Open pipeline" value={fmtMoney(r.payload.open_pipeline)} />
                <ReportStat label="Won revenue" value={fmtMoney(r.payload.won_revenue)} />
                <ReportStat label="Open tickets" value={r.payload.open_tickets} />
                <ReportStat label="Contacts" value={r.payload.contacts} />
                <ReportStat label="Stax active" value={r.payload.stax_active} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const ReportStat = ({ label, value }) => (
  <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
    <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">{label}</div>
    <div className="font-heading text-base font-bold mt-0.5">{value}</div>
  </div>
);

/* ---------------- Placeholder integrations ---------------- */
function PlaceholderPanel({ kind, title, desc, account, reload }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(kind === "chartmogul" ? account.chartmogul_uuid || "" : account.xero_contact_id || "");

  const save = async () => {
    const field = kind === "chartmogul" ? { chartmogul_uuid: val } : { xero_contact_id: val };
    await api.patch(`/accounts/${account.id}`, field);
    toast.success("Mapping saved");
    setEditing(false);
    reload();
  };

  const Icon = kind === "chartmogul" ? BarChart3 : CreditCard;
  const mockMetrics = kind === "chartmogul"
    ? [
        { label: "MRR", value: fmtMoney(account.mrr) },
        { label: "ARR", value: fmtMoney(account.arr) },
        { label: "Active subs", value: "—" },
        { label: "Churn risk", value: "—" },
      ]
    : [
        { label: "Outstanding", value: "—" },
        { label: "Last payment", value: "—" },
        { label: "Overdue invoices", value: "—" },
        { label: "Credit notes", value: "—" },
      ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <Card className="lg:col-span-2 border-slate-200">
        <CardHeader className="border-b border-slate-100 flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Icon className="h-4 w-4 text-blue-600" /> {title}
            </CardTitle>
            <div className="text-xs text-slate-500 mt-1">{desc}</div>
          </div>
          <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
            Phase 3 · Coming soon
          </span>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {mockMetrics.map((m) => (
              <div key={m.label} className="p-4 rounded-md bg-slate-50 border border-slate-100">
                <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">{m.label}</div>
                <div className="font-heading text-xl font-bold mt-1 text-slate-900">{m.value}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 border border-dashed border-slate-200 rounded-md p-6 text-center bg-slate-50/40">
            <Lock className="h-6 w-6 text-slate-300 mx-auto mb-2" />
            <div className="text-sm text-slate-700 font-medium">Live sync not connected</div>
            <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              Once {kind === "chartmogul" ? "ChartMogul" : "Xero"} OAuth is wired up, real subscription{kind === "chartmogul" ? " analytics" : " invoices"} will populate this view automatically.
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-sm font-heading">{kind === "chartmogul" ? "ChartMogul Customer UUID" : "Xero ContactID"}</CardTitle>
        </CardHeader>
        <CardContent className="p-5">
          {!editing ? (
            <>
              <div className="font-mono text-xs text-slate-700 break-all min-h-6">
                {val || <span className="text-slate-400">Not mapped</span>}
              </div>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setEditing(true)}>Edit mapping</Button>
            </>
          ) : (
            <>
              <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={kind === "chartmogul" ? "cm-uuid-..." : "xero-contact-id"} />
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </>
          )}
          <div className="mt-5 text-xs text-slate-500 leading-relaxed">
            <Building2 className="h-3.5 w-3.5 inline -mt-0.5 mr-1 text-slate-400" />
            Mapping links this Xentra account to its external{" "}
            {kind === "chartmogul" ? "ChartMogul" : "Xero"} record.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


/* ---------------- Customer Portal Tab ---------------- */
function PortalTab({ account, reload }) {
  const [enabled, setEnabled] = React.useState(!!account.portal_enabled);
  const [emails, setEmails] = React.useState(account.portal_allowed_emails || []);
  const [input, setInput] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [savedAt, setSavedAt] = React.useState(null);

  const portalUrl = `${window.location.origin}/portal/login`;

  const addEmail = () => {
    const e = input.trim().toLowerCase();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
    if (emails.includes(e)) return;
    setEmails([...emails, e]);
    setInput("");
  };

  const removeEmail = (e) => setEmails(emails.filter((x) => x !== e));

  const save = async () => {
    setSaving(true);
    try {
      // Be forgiving: if there's a valid email typed but not yet added, include it
      let finalEmails = emails;
      const pending = input.trim().toLowerCase();
      if (pending && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pending) && !finalEmails.includes(pending)) {
        finalEmails = [...finalEmails, pending];
        setEmails(finalEmails);
        setInput("");
      }
      if (enabled && finalEmails.length === 0) {
        toast.error("Add at least one allowed email before enabling the portal");
        return;
      }
      await api.patch(`/accounts/${account.id}/portal`, { enabled, allowed_emails: finalEmails });
      toast.success(enabled ? `Portal enabled · ${finalEmails.length} email(s) allowed` : "Portal disabled");
      setSavedAt(Date.now());
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-settings">
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Customer portal access</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Toggle the portal on for this customer and add the email addresses (Google accounts) that should have access. Only listed emails will be allowed to sign in.
          </p>
        </CardHeader>
        <CardContent className="p-5 space-y-5">
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-4">
            <div>
              <div className="font-medium text-slate-900">Portal enabled</div>
              <div className="text-xs text-slate-500 mt-0.5">
                When on, allowed emails can sign in at <a href={portalUrl} className="text-blue-600 hover:underline" target="_blank" rel="noreferrer">{portalUrl}</a>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-testid="portal-toggle">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <div className="w-12 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>

          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Allowed customer emails</div>
            <div className="flex gap-2">
              <input
                type="email"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
                placeholder="finance@customer.com"
                className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="portal-email-input"
              />
              <button
                onClick={addEmail}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                data-testid="portal-email-add"
              >
                Add
              </button>
            </div>
            <div className="mt-3 space-y-1">
              {emails.length === 0 ? (
                <div className="text-xs text-slate-400 italic">No emails yet. Add at least one before enabling portal access.</div>
              ) : (
                emails.map((e) => (
                  <div key={e} className="flex items-center justify-between bg-white border border-slate-200 rounded px-3 py-2 text-sm" data-testid={`portal-email-${e}`}>
                    <span className="font-mono text-slate-700">{e}</span>
                    <button
                      onClick={() => removeEmail(e)}
                      className="text-xs text-red-600 hover:text-red-800"
                      data-testid={`portal-email-remove-${e}`}
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <div className="text-xs text-slate-500">
              {savedAt && <span className="text-emerald-600">Saved at {new Date(savedAt).toLocaleTimeString("en-GB")}</span>}
            </div>
            <button
              onClick={save}
              disabled={saving}
              className="px-5 py-2 bg-slate-900 text-white text-sm rounded-md hover:bg-slate-800 disabled:opacity-50"
              data-testid="portal-save"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">What customers will see</CardTitle>
        </CardHeader>
        <CardContent className="p-5 text-sm text-slate-700 space-y-2">
          <div>· An overview tile of their account, open tickets, projects and live Stax products</div>
          <div>· Full ticket history (read-only) — status, priority, SLA, last update</div>
          <div>· All projects assigned to them and their progress</div>
          <div>· Live Stax products they're using, plus the rest of the catalog available to purchase</div>
          <div>· Historical invoices synced from Xero <span className="text-amber-600 font-medium">(coming next — Xero integration pending)</span></div>
        </CardContent>
      </Card>
    </div>
  );
}
